/* eslint-disable no-param-reassign */
/**
 * Applies table border styles to the currently selected table in the editor.
 * @function
 * @param {Object} editor - The Jodit editor instance.
 * @param {string} mode - Determines the border style to apply. Possible values:
 *   - 'all': Applies a 1px solid border to all table and cells.
 *   - 'outer': Applies a 1px solid border only to the table, removes cell borders.
 *   - 'none': Removes all borders from the table and its cells.
 *
 * The function finds the closest table element from the current selection,
 * then applies styles according to the specified mode.
 *
 * @returns {void}
 */
const applyBorders = (editor, mode) => {
  if (!editor) return;

  const selection = globalThis.getSelection?.();
  if (!selection?.rangeCount) return;

  let node = selection.getRangeAt(0).startContainer;
  if (node?.nodeType === 3) node = node.parentElement;
  if (!node) return;

  const table = node.closest?.('table');
  if (!table) return;

  const cells = table.querySelectorAll('td, th');

  if (mode === 'all') {
    table.style.borderCollapse = 'collapse';
    table.style.border = '1px solid #000';
    cells.forEach((cell) => {
      cell.style.border = '1px solid #000';
    });
  }

  if (mode === 'outer') {
    table.style.borderCollapse = 'collapse';
    table.style.border = '1px solid #000';
    cells.forEach((cell) => {
      cell.style.border = 'none';
    });
  }

  if (mode === 'none') {
    table.style.border = 'none';
    cells.forEach((cell) => {
      cell.style.border = 'none';
    });
  }

  editor.selection.focus();
};

/**
 * Renders a popup with buttons to apply different table border styles.
 * @function
 * @param {Object} editor - The Jodit editor instance.
 * @param {function} close - A function to close the popup.
 * @returns {HTMLElement} The container element containing the buttons.
 */
const renderOptions = (editor, close) => {
  const container = document.createElement('div');
  container.style.padding = '10px';
  container.style.display = 'flex';
  container.style.flexDirection = 'column';
  container.style.gap = '6px';

  const presets = [
    { label: 'Toutes les bordures', mode: 'all' },
    { label: 'Bordure extérieure', mode: 'outer' },
    { label: 'Aucune bordure', mode: 'none' }
  ];

  presets.forEach((preset) => {
    const button = document.createElement('button');
    button.textContent = preset.label;
    button.style.cursor = 'pointer';
    button.onclick = (e) => {
      e.preventDefault();
      e.stopPropagation();
      applyBorders(editor, preset.mode);
      close();
    };
    container.appendChild(button);
  });

  return container;
};

/**
 * Creates a button configuration for the table borders preset.
 * @function
 * @returns {Object} The button configuration.
 */
export const tableBordersButton = () => ({
  name: 'tableBordersPreset',
  tooltip: 'Bordures du tableau',
  text: 'Bordures tableau',
  popup: (editor, _, close) => renderOptions(editor, close)
});
